/**
 * Google Picker API helpers for folder/file selection in Register Source flows.
 * Uses user OAuth (GIS token client) — selected folder IDs are passed to the
 * existing service-account sync backend (folder must be shared with SA email).
 */

export interface GooglePickerSelection {
  id: string;
  name: string;
  mimeType?: string;
  isSharedDrive?: boolean;
}

declare global {
  interface Window {
    google?: {
      picker?: {
        Action: { PICKED: string; CANCEL: string };
        DocsView: new (viewId?: string) => {
          setIncludeFolders: (v: boolean) => unknown;
          setSelectFolderEnabled: (v: boolean) => unknown;
          setMimeTypes: (v: string) => unknown;
          setEnableDrives: (v: boolean) => unknown;
        };
        ViewId: { DOCS: string; FOLDERS: string };
        Feature: { SUPPORT_DRIVES: string; MULTISELECT_ENABLED: string };
        PickerBuilder: new () => {
          addView: (view: unknown) => unknown;
          setOAuthToken: (token: string) => unknown;
          setDeveloperKey: (key: string) => unknown;
          setAppId: (appId: string) => unknown;
          enableFeature: (feature: string) => unknown;
          setCallback: (cb: (data: { action: string; docs?: Array<{ id: string; name: string; mimeType?: string; type?: string }> }) => void) => unknown;
          build: () => { setVisible: (v: boolean) => void };
        };
      };
      accounts?: {
        oauth2?: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: { access_token?: string; error?: string }) => void;
          }) => { requestAccessToken: (opts?: { prompt?: string }) => void };
        };
      };
    };
    gapi?: { load: (name: string, cb: () => void) => void };
  }
}

const PICKER_SCOPE = 'https://www.googleapis.com/auth/drive.readonly';

function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

async function ensurePickerLoaded(): Promise<void> {
  await loadScript('https://apis.google.com/js/api.js', 'google-api-js');
  await loadScript('https://accounts.google.com/gsi/client', 'google-gsi-client');

  await new Promise<void>((resolve, reject) => {
    if (!window.gapi?.load) {
      reject(new Error('Google API client failed to load'));
      return;
    }
    window.gapi.load('picker', () => resolve());
  });
}

export function getGoogleClientId(): string | null {
  const runtime = typeof window !== 'undefined' ? (window as any).RUNTIME_CONFIG : undefined;
  const clientId =
    (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ||
    (runtime?.GOOGLE_CLIENT_ID as string | undefined) ||
    '';
  return clientId || null;
}

export function getGooglePickerConfig(): { clientId: string; apiKey: string } | null {
  const clientId = getGoogleClientId();
  const runtime = typeof window !== 'undefined' ? (window as any).RUNTIME_CONFIG : undefined;
  const apiKey =
    (import.meta.env.VITE_GOOGLE_API_KEY as string | undefined) ||
    (import.meta.env.VITE_GOOGLE_PICKER_API_KEY as string | undefined) ||
    (runtime?.GOOGLE_API_KEY as string | undefined) ||
    '';

  if (!clientId || !apiKey) {
    return null;
  }
  return { clientId, apiKey };
}

async function ensureGoogleIdentityLoaded(): Promise<void> {
  await loadScript('https://accounts.google.com/gsi/client', 'google-gsi-client');
}

/** User-delegated Drive read token (Picker + Drive API list/search). Requires client ID only. */
export async function requestGoogleDriveAccessToken(prompt: '' | 'consent' = ''): Promise<string> {
  const clientId = getGoogleClientId();
  if (!clientId) {
    throw new Error('Google Drive is not configured. Set VITE_GOOGLE_CLIENT_ID in the TraceKit environment.');
  }
  await ensureGoogleIdentityLoaded();
  return requestAccessToken(clientId, prompt);
}

export interface GoogleDriveListItem {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime?: string;
  isFolder: boolean;
}

export interface GoogleDriveListResult {
  items: GoogleDriveListItem[];
  nextPageToken?: string;
}

const FOLDER_MIME = 'application/vnd.google-apps.folder';

/** List folders/files in a Drive folder using the user's OAuth token (no Picker API key required). */
export async function listGoogleDriveItems(options: {
  accessToken: string;
  parentId?: string;
  searchQuery?: string;
  pageToken?: string;
  pageSize?: number;
}): Promise<GoogleDriveListResult> {
  const parent = options.parentId ?? 'root';
  const escapedSearch = options.searchQuery?.trim().replace(/'/g, "\\'");
  let q = `'${parent}' in parents and trashed=false`;
  if (escapedSearch) {
    q += ` and name contains '${escapedSearch}'`;
  }

  const params = new URLSearchParams({
    q,
    fields: 'nextPageToken,files(id,name,mimeType,modifiedTime)',
    orderBy: 'folder,name',
    pageSize: String(options.pageSize ?? 100),
    supportsAllDrives: 'true',
    includeItemsFromAllDrives: 'true',
  });
  if (options.pageToken) {
    params.set('pageToken', options.pageToken);
  }

  const res = await fetch(`https://www.googleapis.com/drive/v3/files?${params}`, {
    headers: { Authorization: `Bearer ${options.accessToken}` },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Drive API list failed (${res.status}): ${body.slice(0, 200)}`);
  }

  const data = (await res.json()) as {
    files?: Array<{ id: string; name: string; mimeType?: string; modifiedTime?: string }>;
    nextPageToken?: string;
  };

  return {
    items: (data.files ?? []).map((file) => ({
      id: file.id,
      name: file.name,
      mimeType: file.mimeType ?? '',
      modifiedTime: file.modifiedTime,
      isFolder: file.mimeType === FOLDER_MIME,
    })),
    nextPageToken: data.nextPageToken,
  };
}

function requestAccessToken(clientId: string, prompt: '' | 'consent' = ''): Promise<string> {
  return new Promise((resolve, reject) => {
    const oauth2 = window.google?.accounts?.oauth2;
    if (!oauth2) {
      reject(new Error('Google Identity Services not available'));
      return;
    }

    const tokenClient = oauth2.initTokenClient({
      client_id: clientId,
      scope: PICKER_SCOPE,
      callback: (response) => {
        if (response.error || !response.access_token) {
          reject(new Error(response.error || 'Google sign-in was cancelled'));
          return;
        }
        resolve(response.access_token);
      },
    });

    tokenClient.requestAccessToken({ prompt });
  });
}

export interface OpenGooglePickerOptions {
  /** When true, allow selecting files as well as folders */
  allowFiles?: boolean;
  /** When true, allow multi-select */
  multiSelect?: boolean;
}

/**
 * Open Google Picker and return selected folder/file metadata.
 */
export async function openGooglePicker(
  options: OpenGooglePickerOptions = {}
): Promise<GooglePickerSelection[]> {
  const config = getGooglePickerConfig();
  if (!config) {
    throw new Error(
      'Google Picker is not configured. Set VITE_GOOGLE_CLIENT_ID and VITE_GOOGLE_API_KEY in the TraceKit environment.'
    );
  }

  await ensurePickerLoaded();
  const accessToken = await requestAccessToken(config.clientId);
  const pickerNs = window.google?.picker;
  if (!pickerNs) {
    throw new Error('Google Picker API unavailable');
  }

  const appId = config.clientId.split('-')[0];

  return new Promise((resolve, reject) => {
    const docsView = new pickerNs.DocsView(pickerNs.ViewId.DOCS);
    docsView.setIncludeFolders(true);
    docsView.setSelectFolderEnabled(true);
    if (!options.allowFiles) {
      docsView.setMimeTypes('application/vnd.google-apps.folder');
    }
    docsView.setEnableDrives(true);

    const builder = new pickerNs.PickerBuilder()
      .addView(docsView)
      .setOAuthToken(accessToken)
      .setDeveloperKey(config.apiKey)
      .setAppId(appId)
      .enableFeature(pickerNs.Feature.SUPPORT_DRIVES);

    if (options.multiSelect) {
      builder.enableFeature(pickerNs.Feature.MULTISELECT_ENABLED);
    }

    builder.setCallback((data) => {
      if (data.action === pickerNs.Action.CANCEL) {
        resolve([]);
        return;
      }
      if (data.action !== pickerNs.Action.PICKED || !data.docs?.length) {
        reject(new Error('No items selected'));
        return;
      }

      resolve(
        data.docs.map((doc) => ({
          id: doc.id,
          name: doc.name,
          mimeType: doc.mimeType,
          isSharedDrive: doc.type === 'teamDrive' || doc.mimeType === 'application/vnd.google-apps.folder',
        }))
      );
    });

    builder.build().setVisible(true);
  });
}
